import type { FerryFavorite, FavoriteRoute, PendingFavoriteEntry } from '@/lib/favoritesStore';
import { pendingEntryToFerryFavorite } from '@/lib/favoritesStore';
import { toSlug } from '@/lib/ferrySlug';


export type MergedSambandFavoriteEntry = {
  slug: string;
  cloudFavorite?: FerryFavorite;
  legacyFavorite?: FerryFavorite;
};

export interface MergeSambandFavoritesOptions {
  /** When true, skip DEV seed while cloud favorites are still loading. */
  isCloudLoading?: boolean;
}

/** Non-legacy cloud rows only (real ferry_favorites IDs). */
export function filterCloudSambandFavorites(favorites: FerryFavorite[]): FerryFavorite[] {
  return favorites.filter((favorite) => !favorite.id.startsWith('legacy-'));
}

function getLegacyRouteSlug(route: FavoriteRoute): string {
  const directSlug = toSlug(route.slug);
  if (directSlug.length > 0) return directSlug;
  return toSlug([route.from, route.to].filter(Boolean).join('-'));
}

export function normalizeLegacyRouteToFerryFavorite(route: FavoriteRoute, index: number): FerryFavorite {
  const sambandSlug = getLegacyRouteSlug(route);

  return {
    id: `legacy-${sambandSlug}`,
    samband_slug: sambandSlug,
    custom_name: null,
    notify_departure_soon: true,
    notify_delay: true,
    notify_cancellation: true,
    notify_time_to_leave: false,
    position: index,
    from: route.from,
    to: route.to,
  };
}

export function resolveFavoriteForRender(
  cloudFavorite?: FerryFavorite | null,
  legacyFavorite?: FerryFavorite | null,
): FerryFavorite | null {
  const preferredFavorite = cloudFavorite ?? legacyFavorite;
  if (!preferredFavorite) return null;

  const preferredSlug = toSlug(preferredFavorite.samband_slug);
  const legacyFallbackSlug = legacyFavorite
    ? toSlug(legacyFavorite.samband_slug || [legacyFavorite.from, legacyFavorite.to].filter(Boolean).join('-'))
    : '';
  const resolvedSlug = preferredSlug || legacyFallbackSlug;

  if (resolvedSlug.length === 0) return null;

  return {
    ...preferredFavorite,
    samband_slug: resolvedSlug,
  };
}

/**
 * Merge cloud + local samband favorites the same way MyFerriesSection renders them.
 * Dedupes by samband_slug; cloud row wins when both exist.
 */
export function mergeSambandFavoritesForRender(
  cloudFavorites: FerryFavorite[],
  legacyRouteFavorites: FavoriteRoute[],
  options: MergeSambandFavoritesOptions = {},
): MergedSambandFavoriteEntry[] {
  const legacyFavorites = legacyRouteFavorites.map((favorite, index) =>
    normalizeLegacyRouteToFerryFavorite(favorite, index),
  );

  const effectiveLegacyFavorites = legacyFavorites;

  const mergedBySlug = new Map<string, { cloudFavorite?: FerryFavorite; legacyFavorite?: FerryFavorite }>();

  for (const favorite of effectiveLegacyFavorites) {
    mergedBySlug.set(favorite.samband_slug, { legacyFavorite: favorite });
  }

  for (const favorite of cloudFavorites) {
    const existing = mergedBySlug.get(favorite.samband_slug);
    mergedBySlug.set(favorite.samband_slug, {
      cloudFavorite: favorite,
      legacyFavorite: existing?.legacyFavorite,
    });
  }

  return Array.from(mergedBySlug.entries()).map(([slug, value]) => ({
    slug,
    cloudFavorite: value.cloudFavorite,
    legacyFavorite: value.legacyFavorite,
  }));
}

/** True when merged samband list is non-empty (same definition as MyFerriesSection content). */
export function hasMergedSambandFavorites(
  cloudFavorites: FerryFavorite[],
  legacyRouteFavorites: FavoriteRoute[],
  options: MergeSambandFavoritesOptions = {},
): boolean {
  return mergeSambandFavoritesForRender(cloudFavorites, legacyRouteFavorites, options).length > 0;
}

/** Gate for mounting MyFerriesSection — matches section's loading + empty behaviour. */
export function shouldShowMyFerriesSection(
  cloudFavorites: FerryFavorite[],
  legacyRouteFavorites: FavoriteRoute[],
  isCloudLoading: boolean,
  pendingQueue: PendingFavoriteEntry[] = [],
): boolean {
  return isCloudLoading
    || hasMergedSambandFavorites(cloudFavorites, legacyRouteFavorites, { isCloudLoading })
    || pendingQueue.length > 0;
}

/**
 * Resolved samband favorites for read paths (unified hook + future wiring).
 * Dedupes via mergeSambandFavoritesForRender; cloud row wins when both exist.
 * Pending rows append when slug not already in cloud (cloud wins over pending).
 */
export function buildUnifiedSambandList(
  cloudFavorites: FerryFavorite[],
  legacyRouteFavorites: FavoriteRoute[],
  options: MergeSambandFavoritesOptions = {},
  pendingQueue: PendingFavoriteEntry[] = [],
): FerryFavorite[] {
  const merged = mergeSambandFavoritesForRender(
    filterCloudSambandFavorites(cloudFavorites),
    legacyRouteFavorites,
    options,
  )
    .map(({ cloudFavorite, legacyFavorite }) => resolveFavoriteForRender(cloudFavorite, legacyFavorite))
    .filter((favorite): favorite is FerryFavorite => favorite !== null);

  const cloudSlugs = new Set(merged.map((favorite) => favorite.samband_slug));
  const pendingRows = pendingQueue
    .filter((entry) => !cloudSlugs.has(entry.samband_slug))
    .map((entry, index) => pendingEntryToFerryFavorite(entry, merged.length + index));

  return [...merged, ...pendingRows];
}
